import { useNavigate } from 'react-router-dom';
import { Button, InlineLoading } from '@carbon/react';
import { Close, CheckmarkOutline, DocumentBlank, Money, Warning, Time } from '@carbon/icons-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNotificationStore } from '@/stores/notification.store';
import { useNotifications } from '@/hooks/useNotifications';
import type { Notification, NotificationType } from '@/types/api.types';

const TYPE_ICON: Record<NotificationType, React.ComponentType<{ size?: number | string }>> = {
  CONTRACT_EXPIRING: DocumentBlank,
  INVOICE_OVERDUE: Money,
  TICKET_SLA_BREACH: Warning,
  PAYMENT_RECEIVED: CheckmarkOutline,
  QUOTE_ACCEPTED: CheckmarkOutline,
  GENERAL: Time,
};

const TYPE_COLOR: Record<NotificationType, string> = {
  CONTRACT_EXPIRING: '#f1a100',
  INVOICE_OVERDUE: '#da1e28',
  TICKET_SLA_BREACH: '#da1e28',
  PAYMENT_RECEIVED: '#1a7f37',
  QUOTE_ACCEPTED: '#1a7f37',
  GENERAL: '#525252',
};

function NotificationItem({ item, onRead }: { item: Notification; onRead: (id: string) => void }) {
  const navigate = useNavigate();
  const Icon = TYPE_ICON[item.type] ?? Time;
  const color = TYPE_COLOR[item.type] ?? '#525252';

  const handleClick = () => {
    if (!item.read) onRead(item.id);
    if (item.link) navigate(item.link);
  };

  return (
    <div
      className={`notif-item${item.read ? ' notif-item--read' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <span className="notif-item-icon" style={{ color }}>
        <Icon size={16} />
      </span>
      <div className="notif-item-body">
        <div className="notif-item-title">{item.title}</div>
        {item.message && <div className="notif-item-message">{item.message}</div>}
        <div className="notif-item-time">
          {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true, locale: fr })}
        </div>
      </div>
      {!item.read && <span className="notif-item-dot" />}
    </div>
  );
}

export function NotificationCenter() {
  const { notifications, unreadCount, isOpen, setOpen } = useNotificationStore();
  const { markRead, markAll } = useNotifications();

  if (!isOpen) return null;

  return (
    <>
      <div className="notif-overlay" onClick={() => setOpen(false)} />
      <div className="notif-panel">
        <div className="notif-panel-header">
          <div>
            <h2 className="notif-panel-title">Notifications</h2>
            {unreadCount > 0 && (
              <span className="notif-panel-count">{unreadCount} non lues</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {unreadCount > 0 && (
              <Button kind="ghost" size="sm" onClick={markAll}>
                Tout marquer lu
              </Button>
            )}
            <Button kind="ghost" size="sm" hasIconOnly renderIcon={Close} iconDescription="Fermer"
              onClick={() => setOpen(false)} />
          </div>
        </div>

        <div className="notif-panel-body">
          {notifications.length === 0 ? (
            <div className="notif-empty">
              <CheckmarkOutline size={32} />
              <p>Aucune notification</p>
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem key={n.id} item={n} onRead={markRead} />
            ))
          )}
        </div>
      </div>
    </>
  );
}
